import React from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, Stack, Switch, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';
import { PdiRow } from './_PdiHelpers';
import { SliderField } from '../../_webshopUi';

// Visual indicator for the current column count — N tinted rectangles
// that scale to fit the container. Lets the admin see at a glance how
// dense the product grid will look without leaving the modal.
const ColumnsPreview = ({ count = 4, max = 8 }) => {
    const cols = Math.max(1, Math.min(max, count));
    return (
        <Box sx={{
            backgroundColor: '#f6f7f9',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 1,
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: 0.75,
            minHeight: 64,
            flex: 1,
        }}>
            {Array.from({ length: cols }).map((_, i) => (
                <Box key={i} sx={{
                    backgroundColor: 'rgba(25,118,210,0.10)',
                    border: '1px solid rgba(25,118,210,0.25)',
                    borderRadius: 0.5,
                    height: 48,
                }} />
            ))}
        </Box>
    );
};
ColumnsPreview.propTypes = { count: PropTypes.number, max: PropTypes.number };

// Tile button for the pagination position picker — Above / Below / Both.
// Renders a small mock of a grid with horizontal lines marking pagination
// position so the admin can see what each option means without copy.
const PaginationTile = ({ label, position, active, onClick }) => {
    // Plain element factories (called as functions, not rendered as <JSX/>):
    // a locally-declared COMPONENT gets a fresh identity per render and React
    // remounts its DOM node — a function call just returns a stable <Box>.
    // (Bar's old `sx` parameter was dead: every call site was a bare Bar().)
    const bar = () => (
        <Box sx={{ height: 3, borderRadius: 0.5, backgroundColor: active ? 'primary.main' : 'rgba(0,0,0,0.55)' }} />
    );
    const body = () => (
        <Box sx={{
            width: '100%', height: 18,
            backgroundColor: active ? 'rgba(25,118,210,0.15)' : 'rgba(0,0,0,0.06)',
            borderRadius: 0.5,
        }} />
    );
    return (
        <ButtonBase
            onClick={onClick}
            sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                p: 1.25,
                width: 92,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: active ? 'primary.main' : 'divider',
                backgroundColor: active ? 'rgba(25,118,210,0.06)' : '#fff',
                color: active ? 'primary.main' : 'text.primary',
                fontWeight: active ? 700 : 500,
                transition: 'all 0.12s',
                '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
            }}
        >
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {position === 'above' && bar()}
                {position === 'both'  && bar()}
                {body()}
                {position === 'below' && bar()}
                {position === 'both'  && bar()}
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 'inherit', color: 'inherit' }}>{label}</Typography>
        </ButtonBase>
    );
};
PaginationTile.propTypes = {
    label:    PropTypes.node,
    position: PropTypes.oneOf(['above', 'below', 'both']).isRequired,
    active:   PropTypes.bool,
    onClick:  PropTypes.func,
};

const ProductGridCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    const maxColumns        = get('maxColumns', 4);
    const paginationPos     = get('paginationPosition', 'below');
    const showTotalProducts = !!get('showTotalProducts', true);

    return (
        <ConfigCard
            anchor="cfg-card-grid-product"
            title={tr('cfg_grid_product_title', 'Product grid')}
            description={tr('cfg_grid_product_desc', 'How products are arranged on the explore page.')}
            theme={theme}
        >
            {/* Maximum columns — slider + numeric readout + visual mock */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: '0 0 220px', minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tr('cfg_grid_max_columns_label', 'Maximum columns')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
                        {tr('cfg_grid_max_columns_help', 'How many products show side by side on large screens. The grid automatically uses fewer columns on smaller screens.')}
                    </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, backgroundColor: '#fafafa', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1.5, py: 1 }}>
                    <SliderField
                        value={maxColumns}
                        min={1} max={8} step={1} marks
                        label={tr('cfg_grid_max_columns_label', 'Maximum columns')}
                        onChange={(v) => set('maxColumns')(v)}
                        sx={{ flex: 1, ml: 1 }}
                    />
                    <ColumnsPreview count={maxColumns} />
                </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />

            <PdiRow
                label={tr('cfg_grid_pagination_label', 'Pagination position')}
                help={tr('cfg_grid_pagination_help', 'Where the page-number controls appear on the explore page')}
                control={
                    <Stack direction="row" spacing={1}>
                        {['above', 'below', 'both'].map((pos) => (
                            <PaginationTile
                                key={pos}
                                position={pos}
                                active={paginationPos === pos}
                                label={tr(`cfg_grid_pagination_${pos}`, pos.charAt(0).toUpperCase() + pos.slice(1))}
                                onClick={() => set('paginationPosition')(pos)}
                            />
                        ))}
                    </Stack>
                }
            />

            <PdiRow
                label={tr('cfg_grid_show_total_label', 'Show total available products')}
                help={tr('cfg_grid_show_total_help', 'Display the total count above the grid (e.g. "24 of 467 products visible")')}
                control={
                    <Switch
                        color="success"
                        size="small"
                        checked={showTotalProducts}
                        onChange={(e) => set('showTotalProducts')(e.target.checked)}
                    />
                }
            />
        </ConfigCard>
    );
};

ProductGridCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default ProductGridCard;
